import {memo, useCallback, useEffect, useMemo, useState} from 'react';
import PageBlock from "../../../components/PageBlock";
import {Button, CardBody, Checkbox} from "@material-tailwind/react";
import DataTable from "../../../components/DataTable/DataTable";
import {API} from "../../../types/API";
import {DataTableRequest} from "../../../types/Requests/API/DataTableRequest";
import {AxiosResponse} from "axios";
import TD from "../../../components/DataTable/TD";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {solid} from "@fortawesome/fontawesome-svg-core/import.macro";
import {useUserContext} from "../../../types/contexts/UserContext";
import {useNavigate} from "react-router-dom";
import {ErrorHandler} from "../../../hooks/errorHandler";
import {useToastContext} from "../../../types/contexts/ToastContext";
import {useErrorHandler} from "react-error-boundary";
import Forbidden from "../../../components/Forbidden";
import {useTranslation} from "react-i18next";
import Card from "../../../components/Card";
import {setTitle} from "../../../hooks/setTitle";
import {Source, SourcesResponse} from "../../../types/Responses/SourcesResponse";
import {APILink} from "../../../types/APILink";
import {checkEnabled2FA} from "../../../hooks/checkEnabled2FA";
import usePersistedDataTableRequest from "../../../hooks/usePersistedDataTableRequest";
import {useDispatch} from "react-redux";
import {setSidebarTitle} from "../../../reducers/sidebar";

const Sources = () => {
    const {t} = useTranslation();
    const user = useUserContext();
    const navigate = useNavigate();
    const toast = useToastContext();
    const dispatch = useDispatch();
    const handleError = useErrorHandler();
    const [sources, setSources] = useState<SourcesResponse>();
    const [isDataTableLoading, setIsDataTableLoading] = useState<boolean>(false);
    const [isForbidden, setIsForbidden] = useState<boolean>(false);
    const defaultRequest = useMemo<DataTableRequest>(() => ({
        length: 10,
        page: 1,
        sort_by: 'id',
        sort_direction: 0
    }), []);
    const [dataTableRequest, persistDataTableRequest] = usePersistedDataTableRequest<DataTableRequest>(defaultRequest);

    const sendRequest = useCallback((request: DataTableRequest) => {
        setIsDataTableLoading(true);
        API.getSources(request).then((response: AxiosResponse) => {
            setSources(response.data as SourcesResponse);
            setIsDataTableLoading(false);
        }).catch((e) => ErrorHandler(e, `get${APILink.GET_SOURCES}`, toast, handleError, setIsForbidden));
    }, [toast, handleError, setIsDataTableLoading]);

    const onDataTableChanged = useCallback((request: DataTableRequest) => {
        persistDataTableRequest(request);
        if (!request.search || request.search.length > 3) {
            sendRequest(!request.search ? {...request, search: ''} : request);
        }
    }, [persistDataTableRequest, sendRequest]);

    const deleteSource = useCallback((source: Source) => {
        if (sources) {
            const filteredArray = {
                ...sources,
                data: sources.data.filter(r => r.id !== source.id),
                count: sources.count - 1
            };
            setIsDataTableLoading(true);
            API.deleteSource(source.id).then(() => {
                if (source) {
                    setSources(filteredArray);
                    setIsDataTableLoading(false);
                }
            }).catch(e => ErrorHandler(e, `delete${APILink.GET_SOURCE.replace('$source', source.id.toString())}`, toast, handleError));
        }
    }, [sources, setIsDataTableLoading, setSources, toast, handleError]);

    const refresh = useCallback(() => {
        sendRequest({...dataTableRequest});
    }, [dataTableRequest]);

    useEffect(() => {
        setTitle(t('admin.sources.title'));
        dispatch(setSidebarTitle(t('admin.sources.title')));
        if (user && user.user) {
            checkEnabled2FA(user.user).then(() => {
                const locationSearch = window.location.search.substr(1).split('&');
                if (locationSearch.length > 0) {
                    const newID = locationSearch[0]?.split('=');
                    if (newID?.length) {
                        const request = {...dataTableRequest, search: newID[1]};
                        persistDataTableRequest(request);
                        sendRequest(request);
                    }
                } else {
                    sendRequest(dataTableRequest);
                }
            }).catch();
        }
    }, [t, user, dispatch]);

    const actionsPanel = useMemo(() => {
        return (<div className="flex flex-row gap-2 lg:justify-end">
                <Button className="bg-transparent text-black dark:text-white shadow-none px-0.5"
                        onClick={() => refresh()}>
                    <FontAwesomeIcon
                        icon={solid('refresh')}
                        className="w-12 h-3"/>
                </Button>
                {user?.user.permissions.includes('create source') &&
                    <Button onClick={() => navigate('/app/admin/sources/create')}
                            className="bg-black text-white">{t('admin.sources.new_source.button')}</Button>}
            </div>
        )
    }, [user, refresh]);

    const dataTable = useMemo(() => {
        if (!sources)
            return <></>;

        return <DataTable {...dataTableRequest}
                          total_pages={sources.pages.total}
                          count={sources.count}
                          nodata={sources.data.length === 0}
                          inProgress={isDataTableLoading}
                          rightPanel={actionsPanel}
                          onChange={onDataTableChanged} headers={[
            {name: '#', sortBy: 'id'},
            {name: t("admin.sources.table.source"), sortBy: 'source'},
            {name: t("admin.sources.table.open_in_new_tab"), sortBy: 'open_in_new_tab'},
            {name: '', className: 'text-right'}
        ]}>
            <tbody>
            {sources.data.map((source, index) => {
                    return <tr key={index}>
                        <TD>{((dataTableRequest.page - 1) * dataTableRequest.length) + index + 1}</TD>
                        <TD>{source.source}</TD>
                        <TD>{source.open_in_new_tab ?
                            <Checkbox color={'teal'} crossOrigin={''} checked={true} disabled={true}/> : ''}</TD>
                        <TD className="text-right">
                            <>
                                {user?.user.permissions.includes('update source') &&
                                    <Button onClick={() => navigate(`/app/admin/sources/${source.id}`)}
                                            className="px-5 py-2 text-white bg-black lg:mr-2"><FontAwesomeIcon
                                        icon={solid("pen-to-square")}/></Button>}
                                {user?.user.permissions.includes('delete source') &&
                                    <Button onClick={() => deleteSource(source)}
                                            className="px-5 py-2 text-red-300 bg-black"><FontAwesomeIcon
                                        icon={solid("trash")}/></Button>}
                            </>
                        </TD>
                    </tr>
                }
            )}
            </tbody>
        </DataTable>
    }, [dataTableRequest, isDataTableLoading, sources, deleteSource, navigate, onDataTableChanged, user?.user.permissions]);

    return (
        <PageBlock>
            <Card className="mt-6 bg-white dark:bg-blue-gray-900 dark:text-gray-500">
                <CardBody className="overflow-auto px-0">
                    {dataTable}
                    <Forbidden isForbidden={isForbidden}/>
                </CardBody>
            </Card>
        </PageBlock>
    );
}
export default memo(Sources);
